import { useState, createContext } from 'react';
import { logInfo } from '../utils/logging';

export type AnimationName =
  | 'product_choice_added'
  | 'product_choice_removed'
  | 'on_variant_change_select'
  | 'first_visit_auto_open_desktop'
  | 'open_popup_desktop'
  | 'open_popup_mobile'
  | 'auto_open_popup_mobile'
  | 'auto_open_popup_desktop'


/**
 * before -> What happen "Before the callback is executed"
 * callback -> Callback is called (click event, input event, any function)
 * after -> What happens "After callback is executed"
 */
type AnimationState = `before` | `callback` | `after`;

const runWithDelay = (done: () => void, delayMs = 100) =>
  setTimeout(done, delayMs);

type TriggerWithAnimationArgs = {
  name: AnimationName;
  delayMsBefore: number;
  delayMsAfter: number;
  data?: unknown;
  callback?: () => void;
};

export interface AnimationContextProps {
  triggerWithAnimation?: (args: TriggerWithAnimationArgs) => void;
  animationState?: AnimationState | null;
  animationName?: AnimationName | null;
  animationData?: unknown | null;
  isAnimationInState: (name: AnimationName, state: AnimationState | null) => boolean
}

const AnimationContext = createContext<AnimationContextProps>({
  isAnimationInState: () => false
});

export function AnimationContextProvider({ children }) {
  const [animationName, setAnimationName] = useState<AnimationName | null>(
    null,
  );
  const [animationState, setAnimationState] = useState<AnimationState | null>(
    null,
  );
  const [animationData, setAnimationData] = useState<unknown | null>(
    null,
  );

  const triggerWithAnimation = <T,>(args: TriggerWithAnimationArgs) => {
    const { name, delayMsBefore, delayMsAfter, callback } = args;
    setAnimationName(name);
    setAnimationState('before');
    setAnimationData(args.data as T)
    logInfo(`[AnimationContext] ${name} before event`)

    runWithDelay(() => {
      setAnimationState('callback');
      logInfo(`[AnimationContext] ${name} event called`)
      callback?.();
    }, delayMsBefore);

    runWithDelay(() => {
      setAnimationState('after');
      logInfo(`[AnimationContext] ${name} after event`)
    }, delayMsAfter + delayMsBefore);

    // clear animations  (add 120ms for them to finish properly)
    // we can use any extra delay .e.g 80ms, 100ms, 120ms to clear them. 120ms works fine currently.
    runWithDelay(() => {
      setAnimationState(null);
      setAnimationName(null);
      setAnimationData(null)
      logInfo(`[AnimationContext] ${name} clearing the animation and animation data`)
    }, delayMsAfter + delayMsBefore + 120);
  };

  const isAnimationInState = (name: AnimationName, state: AnimationState | null) => animationName === name && state === animationState

  return (
    <AnimationContext.Provider
      value={{
        triggerWithAnimation,
        animationName,
        animationState,
        isAnimationInState,
        animationData
      }}
    >
      {children}
    </AnimationContext.Provider>
  );
}

export default AnimationContext;
